/**
 * 图片生成的公共选项常量。
 *
 * 【素材类型维表】MATERIAL_TYPES 是**唯一数据源**：技能浮层、工作台的类型下拉、
 * 提交给后端的 agent / kit_type、卖点字段标签，全部由它派生。
 *
 * 为什么集中：这些值原来散在 Workbench.vue / WorkbenchModeA.vue /
 * WorkbenchModeB.vue 里各有副本，靠「改动时记得同步」维持，
 * 已经漂移出过两套命名并存（promotion 与 promotion_image）、
 * ModeB 的类型表比 ModeA 少两项、靠 AGENT_MAP 兜命名差异这类问题。
 * 加类型 / 改名字现在只改这一处。
 *
 * value 与后端 materials.type / prompt_templates.material_type 一致；
 * 提交给后端的两个值分别是 agent（agent 名）与 kit（kit_type，对应 WorkflowTemplates 的 id）。
 */
export const MATERIAL_TYPES = [
  {
    value: 'main_image', title: '電商主圖', label: '主圖',
    desc: '白底 / 場景主圖，多角度展示', icon: 'el-icon-picture-outline',
    points: '主圖賣點', agent: 'main_image', kit: 'main_image'
  },
  {
    value: 'detail_image', title: '電商詳情圖', label: '詳情圖',
    desc: '按賣點分張，或整頁長圖', icon: 'el-icon-document',
    points: '產品特點', agent: 'detail_image', kit: 'detail_page'
  },
  {
    value: 'sku_image', title: '電商SKU圖', label: 'SKU圖',
    desc: '規格、色號對比展示', icon: 'el-icon-s-grid',
    points: 'SKU賣點', agent: 'sku_image', kit: 'sku_set'
  },
  {
    value: 'promotion_image', title: '電商推廣圖', label: '推廣圖',
    desc: '促銷海報風格，帶文案', icon: 'el-icon-s-flag',
    points: '推廣賣點', agent: 'promotion_image', kit: 'promo_bundle'
  },
  {
    value: 'scene_image', title: '場景圖', label: '場景圖',
    desc: '把產品置入真實使用場景', icon: 'el-icon-picture',
    points: '場景賣點', agent: 'scene_image', kit: 'scene_image'
  }
]

/** 便捷：按某一列拼出 { value: 该列值 } 映射（不用 Object.fromEntries，避免旧内核缺 polyfill） */
function mapOf(field) {
  const out = {}
  MATERIAL_TYPES.forEach(t => { out[t.value] = t[field] })
  return out
}

/** 素材类型下拉：[{ label, value }] */
export const MATERIAL_OPTIONS = MATERIAL_TYPES.map(t => ({ label: t.label, value: t.value }))

/** 技能浮层卡片：[{ value, title, desc, icon }] */
export const SCENE_CARDS = MATERIAL_TYPES.map(t => ({
  value: t.value, title: t.title, desc: t.desc, icon: t.icon
}))

/** 素材类型 → 后端 agent 名 */
export const AGENT_MAP = mapOf('agent')

/** 素材类型 → 后端 kit_type（对应 WorkflowTemplates 的 id） */
export const KIT_MAP = mapOf('kit')

/** 素材类型 → 卖点字段的中文标签 */
export const MATERIAL_LABELS = mapOf('points')

/** 全部素材类型的 value（按界面展示顺序） */
export const MATERIAL_VALUES = MATERIAL_TYPES.map(t => t.value)

/** 取类型短标签；传未知值时原样返回，避免页面出现 undefined */
export function labelOf(value) {
  const t = MATERIAL_TYPES.find(x => x.value === value)
  return t ? t.label : value
}

/**
 * 产品分类（只电商主图用）。
 *
 * 为什么要让用户选：模型认出品类后会按「该品类最常见的样式」画 —— 明说分类
 * 能把它拉住（同类目常见的配色、场景、构图都是干扰项）。不选就不提交，
 * 交给模型自己按商品图判断。要增删直接改这里。
 *
 * **值本身就是繁体**（本平台面向台湾用户）：界面显示与提交给后端的都用繁体，
 * 不经过 OPTION_LABELS_TW —— 后端拿它拼提示词，繁体是用户自己的用语习惯。
 */
export const PRODUCT_CATEGORY_OPTIONS = [
  '服飾鞋包', '美妝個護', '3C數碼', '家居日用', '食品生鮮',
  '母嬰玩具', '運動戶外', '其他'
]

/** 目标群体（只电商主图用）：同上，值即繁体；不选不提交 */
export const TARGET_AUDIENCE_OPTIONS = [
  '上班族', '親子家庭', '商務人士', '學生族', '戶外探險', '運動越野', '銀髮族', '其他'
]

/** 图片风格 */
export const STYLE_OPTIONS = [
  '简约', '高端质感', '促销热卖', '国潮', '清新自然',
  '科技感', '节日氛围', 'ins风', '复古', '手绘插画'
]

/**
 * 选项显示名（台湾繁体）：key 是提交给后端的原值（保持原样），value 是界面显示文字。
 *
 * 这些选项既是界面文字、又是提交后端的参数值（后端按原值匹配模板），
 * 所以只把**显示**换成繁体，value 一律不动。见 twOptionLabel()。
 */
export const OPTION_LABELS_TW = {
  // 图片 / 影片风格
  '简约': '簡約',
  '高端质感': '高端質感',
  '促销热卖': '促銷熱賣',
  '国潮': '國潮',
  '节日氛围': '節日氛圍',
  'ins风': 'ins風',
  'ins 风': 'ins 風',
  '复古': '復古',
  '手绘插画': '手繪插畫',
  '电影感': '電影感',
  '高级灰调': '高級灰調',
  // 影片氛围主题
  '自动': '自動',
  '无': '無',
  '圣诞节 - 温馨雪景': '聖誕節 - 溫馨雪景',
  '农历新年 - 喜庆红金': '農曆新年 - 喜慶紅金',
  '中秋 - 月色团圆': '中秋 - 月色團圓',
  '夏日 - 阳光海滩': '夏日 - 陽光海灘',
  '双11购物节 - 促销狂欢': '雙11購物節 - 促銷狂歡',
  '极简商业 - 白底专业': '極簡商業 - 白底專業',
  '居家 - 温馨暖调': '居家 - 溫馨暖調',
  // 行业品类 / 目标客群
  '户外露营': '戶外露營',
  '保养品': '保養品',
  '食品饮料': '食品飲料',
  '服饰配件': '服飾配件',
  '车用电子': '車用電子',
  '露营玩家': '露營玩家',
  '亲子家庭': '親子家庭',
  '宠物家庭': '寵物家庭',
  '商务人士': '商務人士',
  '银发族': '銀髮族',
  '学生族群': '學生族群'
}

/** 取选项的繁体显示名；没有映射时原样返回（提交值始终用原值，不受影响） */
export function twOptionLabel(v) {
  return OPTION_LABELS_TW[v] || v
}

/** 比例目录：value → { label 按钮短标签, name 中文说明, long 是否长图比例 } */
export const RATIO_CATALOG = {
  '1:1': { label: '1:1', name: '方圖' },
  '3:4': { label: '3:4', name: '豎圖' },
  '4:3': { label: '4:3', name: '橫圖' },
  '2:3': { label: '2:3', name: '豎長圖' },
  '3:2': { label: '3:2', name: '橫長圖' },
  '4:5': { label: '4:5', name: '豎屏' },
  '5:4': { label: '5:4', name: '橫屏' },
  '9:16': { label: '9:16', name: '長豎圖' },
  '16:9': { label: '16:9', name: '橫幅' },
  '21:9': { label: '21:9', name: '超寬幅' },
  '9:21': { label: '9:21', name: '超長豎' },
  '2:1': { label: '2:1', name: '寬幅' },
  '1:2': { label: '1:2', name: '長幅' },
  '1:4': { label: '1:4', name: '長圖（詳情頁一頁到底）', long: true },
  '1:8': { label: '1:8', name: '超長圖（詳情頁）', long: true },
  '4:1': { label: '4:1', name: '超寬幅', long: true },
  '8:1': { label: '8:1', name: '超寬長條', long: true }
}

/** 全部比例（模型由后端按参数自动选，参数不支持时后端会自动改用支持的模型） */
export const ALL_RATIOS = [
  '1:1', '3:4', '4:3', '2:3', '3:2', '4:5', '5:4', '9:16', '16:9', '21:9',
  '9:21', '2:1', '1:2',
  '1:4', '4:1', '1:8', '8:1'
]

/** 首页常用的 9 个比例（其余放进「更多比例」） */
export const COMMON_RATIOS = ['1:1', '3:4', '4:3', '9:16', '16:9', '2:3', '3:2', '4:5', '21:9']

/** 分辨率档位 */
export const QUALITY_OPTIONS = ['1K', '2K', '4K']

/** 便捷：拼出 { value, label, name, long } 的比例列表 */
export function ratioOptionList() {
  return ALL_RATIOS.map(v => {
    const c = RATIO_CATALOG[v] || { label: v, name: '' }
    return { value: v, label: c.label, name: c.name, long: !!c.long }
  })
}
